// Inspection Device Settings — enable Bluetooth/Ethernet/USB auto-connect
// for measuring instruments (calipers, micrometers, gauges, CMMs).
// Saves device whitelist to TB so all inspection screens know what's paired.
import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Stack, Chip, Switch, Button, IconButton, LinearProgress,
  Divider, Alert, FormControlLabel, MenuItem, TextField,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import LanIcon from '@mui/icons-material/Lan';
import UsbIcon from '@mui/icons-material/Usb';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const SETTINGS_KEY = 'inspection_device_settings';

// What the page knows how to talk to.
const SUPPORTED = [
  { type: 'Bluetooth', icon: 'bt', vendor: 'Mitutoyo', model: 'U-WAVE / SP-1', usage: 'Caliper, Micrometer' },
  { type: 'Bluetooth', icon: 'bt', vendor: 'Sylvac', model: 'Bluetooth gauge', usage: 'Indicators, depth gauges' },
  { type: 'Bluetooth', icon: 'bt', vendor: 'Bowers', model: 'BlueBORE', usage: 'Bore gauges' },
  { type: 'Ethernet',  icon: 'eth', vendor: 'Zeiss', model: 'CALYPSO CMM', usage: 'CMM (TCP)' },
  { type: 'Ethernet',  icon: 'eth', vendor: 'Hexagon', model: 'PC-DMIS', usage: 'CMM (TCP)' },
  { type: 'USB',       icon: 'usb', vendor: 'Generic', model: 'HID Caliper', usage: 'Caliper (USB-Serial)' },
  { type: 'RS-232',    icon: 'usb', vendor: 'Mitutoyo', model: 'Digimatic SPC', usage: 'Legacy SPC port' },
];

// Mock "discoverable" devices — replaces real BT scan API for demo.
const MOCK_NEARBY = [
  { id: 'BT-2A:4F:1C:8D:E2:11', type: 'Bluetooth', name: 'Mitutoyo U-WAVE', model: 'SP-1', signal: -52, vendor: 'Mitutoyo' },
  { id: 'BT-7C:6B:9A:01:5E:F0', type: 'Bluetooth', name: 'Sylvac BT-Gauge',  model: 'S_Cal EVO', signal: -68, vendor: 'Sylvac' },
  { id: 'BT-44:21:11:8A:90:1C', type: 'Bluetooth', name: 'Bowers BlueBORE', model: 'XT3-BT-50',   signal: -71, vendor: 'Bowers' },
  { id: 'ETH-192.168.0.43',    type: 'Ethernet',  name: 'CMM-01 (Zeiss Contura)', model: 'CALYPSO 2024', signal: null, vendor: 'Zeiss' },
];

const InspectionDeviceSettings = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [settings, setSettings] = useState({
    autoConnect: true,
    bluetoothEnabled: true,
    ethernetEnabled: true,
    usbEnabled: true,
    autoLogReadings: true,
    paired: [], // array of {id, type, name, vendor, model, addedAt}
  });
  const [scanning, setScanning] = useState(false);
  const [discovered, setDiscovered] = useState([]);

  // Load saved settings
  useEffect(() => {
    (async () => {
      try {
        const r = await customerbasedshift(cid, SETTINGS_KEY);
        const v = r?.[0]?.value;
        if (v && typeof v === 'object') setSettings((prev) => ({ ...prev, ...v }));
      } catch { /* ignore */ }
    })();
  }, [cid]);

  const save = async (next) => {
    setSettings(next);
    try { await shiftadd({ [SETTINGS_KEY]: next }, cid, 'SERVER_SCOPE'); } catch (e) { console.error(e); }
  };

  const startScan = () => {
    setScanning(true);
    setDiscovered([]);
    // Reveal devices one-by-one to look like a real scan
    MOCK_NEARBY.forEach((d, i) => {
      setTimeout(() => {
        setDiscovered((prev) => [...prev, d]);
        if (i === MOCK_NEARBY.length - 1) setScanning(false);
      }, 700 + i * 600);
    });
  };

  const pair = (dev) => {
    if (settings.paired.find(p => p.id === dev.id)) return;
    const next = { ...settings, paired: [...settings.paired, { ...dev, addedAt: Date.now() }] };
    save(next);
  };

  const unpair = (id) => {
    save({ ...settings, paired: settings.paired.filter(p => p.id !== id) });
  };

  const renderIcon = (type) => {
    if (type === 'Bluetooth') return <BluetoothIcon sx={{ color: '#1d4ed8' }} />;
    if (type === 'Ethernet')  return <LanIcon       sx={{ color: '#16a34a' }} />;
    if (type === 'USB')       return <UsbIcon       sx={{ color: '#7c3aed' }} />;
    return <UsbIcon sx={{ color: '#6b7280' }} />;
  };

  const signalLabel = (rssi) => {
    if (rssi === null || rssi === undefined) return null;
    if (rssi >= -60) return 'Strong';
    if (rssi >= -75) return 'Good';
    return 'Weak';
  };
  const signalColor = (rssi) => {
    if (rssi === null || rssi === undefined) return '#6b7280';
    if (rssi >= -60) return '#16a34a';
    if (rssi >= -75) return '#f59e0b';
    return '#dc2626';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <SensorsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
          Inspection Device Settings
        </Typography>
        <Chip
          icon={settings.autoConnect ? <CheckCircleIcon /> : <WarningAmberIcon />}
          label={settings.autoConnect ? 'Auto-connect ON' : 'Auto-connect OFF'}
          color={settings.autoConnect ? 'success' : 'warning'}
          size="small"
          sx={{ ml: 'auto', fontWeight: 800 }}
        />
      </Stack>

      <Alert severity={settings.autoConnect ? 'success' : 'info'} sx={{ mb: 2, fontWeight: 600 }}>
        {settings.autoConnect
          ? `Auto-connect is ON. Paired devices (${settings.paired.length}) will reconnect automatically when in range. Readings auto-fill the inspection screens.`
          : 'Auto-connect is OFF. Devices need to be connected manually each time.'}
      </Alert>

      {/* Top: master switches */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ p: 2, flex: 1, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1 }}>
            CONNECTION CHANNELS
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch checked={settings.bluetoothEnabled} onChange={(e) => save({ ...settings, bluetoothEnabled: e.target.checked })} />}
              label={<Stack direction="row" alignItems="center" spacing={1}><BluetoothIcon fontSize="small" sx={{ color: '#1d4ed8' }} /><Typography sx={{ fontWeight: 700 }}>Bluetooth</Typography></Stack>}
            />
            <FormControlLabel
              control={<Switch checked={settings.ethernetEnabled} onChange={(e) => save({ ...settings, ethernetEnabled: e.target.checked })} />}
              label={<Stack direction="row" alignItems="center" spacing={1}><LanIcon fontSize="small" sx={{ color: '#16a34a' }} /><Typography sx={{ fontWeight: 700 }}>Ethernet / TCP</Typography></Stack>}
            />
            <FormControlLabel
              control={<Switch checked={settings.usbEnabled} onChange={(e) => save({ ...settings, usbEnabled: e.target.checked })} />}
              label={<Stack direction="row" alignItems="center" spacing={1}><UsbIcon fontSize="small" sx={{ color: '#7c3aed' }} /><Typography sx={{ fontWeight: 700 }}>USB / RS-232</Typography></Stack>}
            />
          </Stack>
        </Card>
        <Card sx={{ p: 2, flex: 1, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1 }}>
            BEHAVIOUR
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch checked={settings.autoConnect} onChange={(e) => save({ ...settings, autoConnect: e.target.checked })} />}
              label={<Typography sx={{ fontWeight: 700 }}>Auto-connect paired devices on enter</Typography>}
            />
            <FormControlLabel
              control={<Switch checked={settings.autoLogReadings} onChange={(e) => save({ ...settings, autoLogReadings: e.target.checked })} />}
              label={<Typography sx={{ fontWeight: 700 }}>Auto-fill measurement field from latest reading</Typography>}
            />
          </Stack>
        </Card>
      </Stack>

      {/* Paired devices */}
      <Card sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', letterSpacing: 1 }}>
            PAIRED DEVICES ({settings.paired.length})
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Chip label="All inspection screens use these" size="small" sx={{ fontWeight: 700 }} />
          </Box>
        </Stack>
        {settings.paired.length === 0 && (
          <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>
            No devices paired yet. Scan and pair below to enable auto-readings.
          </Typography>
        )}
        {settings.paired.map((d) => (
          <Box key={d.id} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25,
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            {renderIcon(d.type)}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                {d.name} {d.vendor && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>{d.vendor}</span>}
              </Typography>
              <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
                {d.id} · {d.model}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              icon={<LinkIcon />}
              label="Connected · auto-reconnect"
              size="small"
              color="success"
              sx={{ fontWeight: 800 }}
            />
            <IconButton size="small" title="Unpair device" onClick={() => unpair(d.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Card>

      {/* Scan / discover */}
      <Card sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', letterSpacing: 1 }}>
            DISCOVER NEW DEVICES
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            disabled={scanning}
            onClick={startScan}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {scanning ? 'Scanning…' : 'Scan nearby'}
          </Button>
        </Stack>
        {scanning && <LinearProgress sx={{ mb: 1 }} />}
        {discovered.length === 0 && !scanning && (
          <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>
            Click "Scan nearby" to look for Bluetooth + Ethernet measuring instruments.
          </Typography>
        )}
        {discovered.map((d) => {
          const paired = !!settings.paired.find(p => p.id === d.id);
          const sigLbl = signalLabel(d.signal);
          const sigClr = signalColor(d.signal);
          return (
            <Box key={d.id} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25,
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {renderIcon(d.type)}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
                  {d.name}
                  <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>{d.vendor}</span>
                </Typography>
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
                  {d.id} · {d.model}
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
              {sigLbl && (
                <Chip
                  label={`${sigLbl} ${d.signal} dBm`}
                  size="small"
                  sx={{ fontWeight: 700, bgcolor: sigClr + '22', color: sigClr }}
                />
              )}
              {paired ? (
                <Chip icon={<CheckCircleIcon />} label="Paired" color="success" size="small" sx={{ fontWeight: 800 }} />
              ) : (
                <Button size="small" variant="contained" onClick={() => pair(d)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                  Pair
                </Button>
              )}
            </Box>
          );
        })}
      </Card>

      {/* Supported instruments reference */}
      <Card sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1.5 }}>
          SUPPORTED INSTRUMENT FAMILIES
        </Typography>
        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5,
        }}>
          {SUPPORTED.map((s, i) => (
            <Box key={i} sx={{
              p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2,
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              {renderIcon(s.type)}
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: 'text.primary' }}>{s.vendor} {s.model}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{s.type} · {s.usage}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

export default InspectionDeviceSettings;
